import { type ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';

import type { FooterActionsType } from '@interstate/components/FooterActions';
import { Typography } from '@interstate/components/Typography';
import { Spinner } from '@interstate/components/Spinner';
import { Grid } from '@interstate/components/Grid';
import { ModalHiddenFloatingFooter } from './GenerateContract.style';
import { GenerateContractProgressMessage } from './GenerateContractProgressMessage';

export const GenerateContract = () => {
    const isActive = useSelector(contractingSelectors.isContractingModalOpen);
    const isInProgress = useSelector(contractingSelectors.isContractingInProgress);
    const dispatch = useDispatch();
    const [isShowingCloseConfirmation, setIsShowingCloseConfirmation] = useState(false);

    if (!isActive) {
        return null;
    }

    let body: ReactElement | string = isInProgress ? (
        <Grid
            container
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
            textAlign="center"
            rowGap={'1rem'}
            marginBottom={'4rem'}
        >
            <Spinner />
            <GenerateContractProgressMessage />
        </Grid>
    ) : (
        'You are about to generate a contact. Proceeding with this action will cancel ' +
        'all existing contracts. Are you sure you want to proceed?'
    );

    const showFooter = !isInProgress || undefined;
    let footer: FooterActionsType | undefined = showFooter && {
        options: [
            {
                action: () => {
                    dispatch(contractingActionCreators.contractingCloseModal());
                },
                label: 'Cancel',
                buttonStyle: 'tertiary'
            }
        ],
        primary: {
            action: () => {
                dispatch(contractingActionCreators.contractingStartProgress());
                dispatch(interactionAnalyticsActionCreators.generateContractConfirmButton());
            },
            label: 'Proceed'
        }
    };

    if (isShowingCloseConfirmation) {
        body = (
            <div>
                <strong data-testid="close-modal-close-confirmation-header">Are you sure you want to cancel?</strong>
                <p style={{ margin: '1rem 0' }}>
                    Taking this action will stop the system from monitoring contract generation. Contracts may be completed
                    successfully. However, if you make any changes to the deal, it is recommended that you regenerate the contract.
                </p>
            </div>
        );
        footer = {
            options: [
                {
                    action: () => {
                        setIsShowingCloseConfirmation(false);
                        dispatch(contractingActionCreators.contractingCloseModal());
                    },
                    label: 'Yes',
                    buttonStyle: 'tertiary'
                }
            ],
            primary: {
                action: () => setIsShowingCloseConfirmation(false),
                label: 'No'
            }
        };
    }

    return (
        <ModalHiddenFloatingFooter
            data-testid="generate-contract-dialog"
            show={true}
            size="large"
            onHide={() => {
                if (isInProgress) {
                    setIsShowingCloseConfirmation(true);
                } else {
                    dispatch(contractingActionCreators.contractingCloseModal());
                }
            }}
            isShowingCloseConfirmation={isShowingCloseConfirmation}
            header={
                <Typography variant="h3" color="sem.color.on-surface.default">
                    Generate Contract
                </Typography>
            }
            footer={footer}
        >
            {body}
        </ModalHiddenFloatingFooter>
    );
};
